import { z } from 'zod';

/**
 * Environment variable validation schema
 * Ensures all required config is present at build/runtime
 */
const envSchema = z.object({
  // Supabase Configuration (Required)
  VITE_SUPABASE_URL: z.string().url('Invalid Supabase URL format'),
  VITE_SUPABASE_PUBLISHABLE_KEY: z.string().min(1, 'Supabase publishable key is required'),
  VITE_SUPABASE_PROJECT_ID: z.string().min(1, 'Supabase project ID is required'),

  // Optional: Error Monitoring (Phase 2)
  VITE_SENTRY_DSN: z.string().url().optional(),
  VITE_SENTRY_ENVIRONMENT: z.enum(['development', 'staging', 'production']).optional(),

  // Optional: Feature Flags
  VITE_ENABLE_AI_FEATURES: z.string().transform(val => val === 'true').optional(),
  VITE_ENABLE_EMAIL_NOTIFICATIONS: z.string().transform(val => val === 'true').optional(),
});

/**
 * Validate and parse environment variables
 * Throws descriptive error if validation fails
 */
function validateEnv() {
  try {
    return envSchema.parse(import.meta.env);
  } catch (error) {
    if (error instanceof z.ZodError) {
      const missingVars = error.errors.map(e => `  - ${e.path.join('.')}: ${e.message}`).join('\n');
      
      console.error(
        '❌ Environment variable validation failed:\n\n' +
        missingVars +
        '\n\nPlease check your .env file matches .env.example\n'
      );
      
      throw new Error('Invalid environment configuration. See console for details.');
    }
    throw error;
  }
}

/**
 * Validated environment variables
 * Use this instead of import.meta.env for type safety
 */
export const env = validateEnv();

/**
 * Type-safe environment access
 */
export type Env = z.infer<typeof envSchema>;
